import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  Clock,
  Shield,
} from 'lucide-react'
import { statusReasonCodeLabel } from '../operatorUiCopy'
import { decodeHtmlEntities } from '../../../utils/displayText'

// ── Constants ──────────────────────────────────────────────────

export const STATUS_META = {
  not_started: {
    label: 'Not started',
    chipClass: 'bg-gray-100 text-gray-700',
    cardClass: 'border-slate-200 bg-slate-50 text-slate-900',
    icon: Clock,
  },
  in_progress: {
    label: 'In progress',
    chipClass: 'bg-primary-100 text-primary-700',
    cardClass: 'border-primary-200 bg-primary-50 text-primary-900',
    icon: Clock,
  },
  covered: {
    label: 'Covered',
    chipClass: 'bg-emerald-100 text-emerald-700',
    cardClass: 'border-emerald-200 bg-emerald-50 text-emerald-900',
    icon: CheckCircle,
  },
  covered_weak_confidence: {
    label: 'Covered (weak)',
    chipClass: 'bg-emerald-50 text-emerald-600 ring-1 ring-emerald-200',
    cardClass: 'border-emerald-200 border-dashed bg-emerald-50/50 text-emerald-800',
    icon: CheckCircle,
  },
  at_risk: {
    label: 'At risk',
    chipClass: 'bg-red-100 text-red-700',
    cardClass: 'border-red-200 bg-red-50 text-red-900',
    icon: AlertTriangle,
  },
}

export const HIDDEN_PRIVATE_NOTIFICATION_CODES = new Set([
  'time_warning_while_speaking',
  'wrapup_while_speaking',
  'overtime_while_speaking',
])

export const HIDDEN_TOPIC_REASON_CODES = new Set([
  'linked_topic_active_without_decision',
  'linked_topic_advanced_without_decision',
  'linked_topic_has_decision',
])

export const HIDDEN_SHARED_SIGNAL_CODES = new Set([
  'active_topic_unresolved',
  'topic_moved_without_decision',
  'unanswered_question', // Questions section already shows open questions with full context
  'agenda_drift',
])

// ── Type-aware meta for Zone 2 cards ──────────────────────────

export const NOTIFICATION_CODE_META = {
  interrupt_warning:           { label: 'Interruption',   accent: 'amber' },
  interrupt_soft_mute:         { label: 'Auto-muted',     accent: 'red' },
  interrupt_hard_mute:         { label: 'Hard mute',      accent: 'red' },
  tangent_nudge:               { label: 'Off-topic',      accent: 'amber' },
  tangent_escalation:          { label: 'Off-topic',      accent: 'red' },
  overtalk_nudge:              { label: 'Airtime',        accent: 'amber' },
  time_warning_while_speaking: { label: 'Time warning',   accent: 'amber' },
  wrapup_while_speaking:       { label: 'Wrap up',        accent: 'amber' },
  overtime_while_speaking:     { label: 'Over time',      accent: 'red' },
}

export const QUEUE_CODE_META = {
  viewer_link_pending:           { label: 'Link pending',    accent: 'slate' },
  close_decision_while_speaking: { label: 'Decision needed', accent: 'red' },
  keep_topic_open:               { label: 'Keep topic open', accent: 'blue' },
  confirm_weak_coverage:         { label: 'Weak coverage',   accent: 'amber' },
  recover_at_risk_topic:         { label: 'At risk',         accent: 'red' },
  watch_shared_goal_risk:        { label: 'Goal at risk',    accent: 'red' },
}

export function formatRelativeTime(ms) {
  if (!ms) return null
  const diffMs = Date.now() - ms
  const diffMin = Math.round(diffMs / 60000)
  if (diffMin < 1) return 'just now'
  if (diffMin === 1) return '1m ago'
  if (diffMin < 60) return `${diffMin}m ago`
  const diffHr = Math.round(diffMin / 60)
  return `${diffHr}h ago`
}

export const QUESTION_TYPE_LABELS = {
  clarification: 'Clarification',
  decision_seeking: 'Decision',
  information_seeking: 'Info',
  challenge: 'Challenge',
  follow_up: 'Follow-up',
}

// ── Render helpers ─────────────────────────────────────────────

export function formatStatusCount(label, value) {
  return `${label}: ${value}`
}

export function renderEvidenceLabel(row) {
  const parts = []
  if (row.decisionCount > 0) parts.push(`${row.decisionCount} decision${row.decisionCount === 1 ? '' : 's'}`)
  if (row.actionItemCount > 0) parts.push(`${row.actionItemCount} action item${row.actionItemCount === 1 ? '' : 's'}`)
  if (row.followUpCount > 0) parts.push(`${row.followUpCount} follow-up${row.followUpCount === 1 ? '' : 's'}`)
  if (parts.length === 0) return null
  return parts.join(' \u2022 ')
}

export function hasCapturedOutputs(row) {
  return row.decisionCount + row.actionItemCount + row.followUpCount > 0
}

export function formatReminderTime(ms) {
  if (!ms) return null
  try {
    return new Date(ms).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
  } catch {
    return null
  }
}

export function renderSharedActionState(actionState) {
  if (actionState.status === 'open') return null
  const actor = actionState.actedBy?.displayName || 'A teammate'
  const remindTime = formatReminderTime(actionState.remindUntilMs)
  if (actionState.status === 'acknowledged') return `${actor} acknowledged this for the team.`
  if (actionState.status === 'claimed') return `${actor} is owning this shared intervention.`
  if (actionState.status === 'resolved') return `${actor} marked this shared item resolved.`
  if (actionState.status === 'remind_later') {
    return remindTime ? `${actor} snoozed this for the team until ${remindTime}.` : `${actor} snoozed this for the team.`
  }
  return null
}

export function renderPersonalActionState(actionState) {
  if (actionState.status === 'open') return null
  const remindTime = formatReminderTime(actionState.remindUntilMs)
  if (actionState.status === 'completed') return 'You marked this private prompt done.'
  if (actionState.status === 'dismissed') return 'You dismissed this private prompt.'
  if (actionState.status === 'snoozed') {
    return remindTime ? `You snoozed this private prompt until ${remindTime}.` : 'You snoozed this private prompt.'
  }
  return null
}

export function renderPrivateNotificationCategory(category) {
  if (category === 'time_management') return 'time management'
  return 'behavior'
}

export function renderPrivateNotificationTime(createdAtMs) {
  if (!createdAtMs) return null
  try {
    return new Date(createdAtMs).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
  } catch {
    return null
  }
}

export function renderReasonLabel(reasonCode) {
  return statusReasonCodeLabel(reasonCode)
}

export function renderReasonBadgeMeta(reasonCode) {
  const label = renderReasonLabel(reasonCode)
  if (reasonCode === 'agenda_drift_elevated') {
    return {
      label,
      className: 'bg-amber-100 text-amber-800 ring-1 ring-amber-200',
    }
  }
  return {
    label,
    className: 'bg-white/90 text-slate-600 ring-1 ring-slate-200/80',
  }
}

export function renderSharedSignalLabel(signal) {
  if (signal.code === 'agenda_drift') return 'Agenda Drift'
  if (signal.code === 'shared_internal_goal_at_risk') return 'Internal Goal at Risk'
  if (signal.code === 'active_topic_unresolved') return 'Current Topic Unresolved'
  if (signal.code === 'topic_moved_without_decision') return 'Moved On Without Decision'
  return signal.code.replaceAll('_', ' ')
}

export function renderSharedActionChipLabel(actionState) {
  if (actionState.status === 'acknowledged') return 'Acknowledged'
  if (actionState.status === 'claimed') return 'Owned'
  if (actionState.status === 'resolved') return 'Resolved'
  if (actionState.status === 'remind_later') {
    return formatReminderTime(actionState.remindUntilMs) ? `Remind ${formatReminderTime(actionState.remindUntilMs)}` : 'Remind later'
  }
  return null
}

export function renderQuestionScopeLabel(scope) {
  if (scope === 'internal_team') return 'Internal team'
  return 'External or unknown'
}

export function renderToneUpdatedAt(updatedAtMs) {
  if (!updatedAtMs) return null
  try {
    return new Date(updatedAtMs).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
  } catch {
    return null
  }
}

export function normalizeParticipantLookupValue(value) {
  if (typeof value !== 'string') return ''
  return value.trim().toLowerCase()
}

export function findParticipantControlTarget(toneRead, participants) {
  const targetParticipantId = normalizeParticipantLookupValue(toneRead?.participantId)
  const targetDisplayName = normalizeParticipantLookupValue(toneRead?.displayName)
  if (!targetParticipantId && !targetDisplayName) return null

  return (
    participants.find((participant) => {
      const participantId = normalizeParticipantLookupValue(participant?.participantId ?? participant?.id)
      if (targetParticipantId && participantId === targetParticipantId) return true

      const participantDisplayName = normalizeParticipantLookupValue(
        participant?.displayName ?? participant?.name ?? participant?.userName ?? participant?.user_name
      )
      return Boolean(targetDisplayName && participantDisplayName === targetDisplayName)
    }) ?? null
  )
}

export function getToneSignalMeta(signal) {
  if (!signal) {
    return {
      label: 'No tone read yet',
      chipClass: 'bg-slate-100 text-slate-600',
      cardClass: 'border-slate-200 border-dashed bg-slate-50/80 text-slate-600',
      icon: Shield,
    }
  }
  switch (signal.status) {
    case 'constructive':
      return {
        label: 'Constructive',
        chipClass: 'bg-emerald-100 text-emerald-700',
        cardClass: 'border-emerald-200 bg-emerald-50 text-emerald-900',
        icon: CheckCircle,
      }
    case 'negative':
      return {
        label: 'Negative watch',
        chipClass: 'bg-amber-100 text-amber-700',
        cardClass: 'border-amber-200 bg-amber-50 text-amber-900',
        icon: AlertTriangle,
      }
    case 'contentious':
      return {
        label: 'Contention watch',
        chipClass: 'bg-orange-100 text-orange-700',
        cardClass: 'border-orange-200 bg-orange-50 text-orange-900',
        icon: AlertTriangle,
      }
    case 'argumentative':
      return {
        label: 'Argumentative watch',
        chipClass: 'bg-red-100 text-red-700',
        cardClass: 'border-red-200 bg-red-50 text-red-900',
        icon: AlertTriangle,
      }
    case 'neutral':
      return {
        label: 'Neutral',
        chipClass: 'bg-slate-100 text-slate-700',
        cardClass: 'border-slate-200 bg-slate-50 text-slate-900',
        icon: Shield,
      }
    default:
      return {
        label: 'Insufficient evidence',
        chipClass: 'bg-slate-100 text-slate-700',
        cardClass: 'border-slate-200 bg-slate-50 text-slate-900',
        icon: Shield,
      }
  }
}

// ── Reusable components ────────────────────────────────────────

export function useAutoCollapse(hasLiveData, collapseWhenQuiet = true) {
  const [manualCollapsed, setManualCollapsed] = useState(null)
  const collapsed = manualCollapsed !== null ? manualCollapsed : (collapseWhenQuiet && !hasLiveData)

  const toggleCollapsed = () => {
    setManualCollapsed(!collapsed)
  }

  return [collapsed, toggleCollapsed]
}

export function ReadOnlyActionButton({ onAttempt, children, className = '' }) {
  return (
    <button
      type="button"
      onClick={onAttempt}
      aria-disabled="true"
      title="Available only while the meeting is live"
      className={`cursor-not-allowed rounded-lg border border-gray-200 bg-gray-100 px-2.5 py-1 text-[11px] font-medium text-gray-400 opacity-90 grayscale transition-colors ${className}`}
    >
      {children}
    </button>
  )
}

export function InsightChip({ children, className = '', title }) {
  return (
    <span
      title={title}
      className={`inline-flex min-h-6 min-w-0 max-w-full items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium leading-4 ${className}`}
    >
      {children}
    </span>
  )
}

export function InsightMetaRow({ leading = null, trailing = null }) {
  const hasLeading = leading !== null
  const hasTrailing = trailing !== null

  if (!hasLeading && !hasTrailing) return null

  return (
    <div className={`grid gap-2 ${hasLeading && hasTrailing ? 'sm:grid-cols-[minmax(0,1fr)_auto] sm:items-start' : ''}`}>
      {hasLeading ? <div className="flex flex-wrap gap-2">{leading}</div> : null}
      {hasTrailing ? <div className="flex flex-wrap gap-2 sm:justify-end">{trailing}</div> : null}
    </div>
  )
}

export function InsightWidget({
  icon: Icon,
  title,
  description,
  shellClass = '',
  iconWrapClass = '',
  iconClass = '',
  headerAside = null,
  summary = null,
  collapsed = false,
  onToggle = null,
  fillBody = false,
  children,
}) {
  return (
    <section className={`flex min-h-0 flex-col overflow-hidden rounded-2xl border p-4 shadow-sm ${fillBody ? '' : 'shrink-0'} ${shellClass}`}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl ${iconWrapClass}`}>
            <Icon className={`h-[18px] w-[18px] ${iconClass}`} />
          </div>
          <div className="min-w-0">
            <h4 className="text-sm font-semibold text-slate-900">{title}</h4>
            <p className="mt-1 text-xs leading-5 text-slate-600">{description}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:justify-end">
          {headerAside}
          {onToggle ? (
            <button
              type="button"
              onClick={onToggle}
              aria-expanded={!collapsed}
              className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white/90 px-2.5 py-1 text-[11px] font-semibold text-slate-600 transition-colors hover:border-slate-300 hover:text-slate-900"
            >
              {collapsed ? 'Show' : 'Hide'}
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${collapsed ? '' : 'rotate-180'}`} />
            </button>
          ) : null}
        </div>
      </div>
      {summary ? <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-700">{summary}</div> : null}
      <AnimatePresence initial={false}>
        {!collapsed ? (
          <motion.div
            key="widget-body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className={`mt-3 ${fillBody ? 'flex min-h-0 flex-1 flex-col' : ''}`}>
              {children}
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </section>
  )
}

// Re-export decodeHtmlEntities for convenience
export { decodeHtmlEntities }
